import clsx from 'clsx'
import type {
  AriaAttributes,
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  ForwardedRef,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react'
import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDebouncedCallback } from 'use-debounce'
import { useId } from '../hooks/useId'
import { useSyncRefs } from '../hooks/useSyncRefs'
import IconButton from '../IconButton/IconButton'
import { Close } from '../icons'
import { isFocusWithinSupported } from '../utilities/isFocusWithinSupported'
import { toDataSet } from '../utilities/toDataSet'
import type {
  TextInputSize,
  TextInputStatus,
  TextInputVariant,
  TextInputColor,
} from './styles'
import {
  adornments,
  baseClasses,
  labels,
  sizes,
  variants,
  multilineSizes,
  inputVariants,
  focusedStyles,
  parentSizes,
  appearFocusedStyles,
  labelSpacing,
  labelColors,
} from './styles'

type EnterKeyHint =
  | 'enter'
  | 'done'
  | 'go'
  | 'next'
  | 'previous'
  | 'search'
  | 'send'

type InputMode =
  | 'none'
  | 'text'
  | 'tel'
  | 'url'
  | 'email'
  | 'numeric'
  | 'decimal'
  | 'search'

type SvgIconComponent = {
  uiName: 'SvgIcon'
  (props: { color: string }): JSX.Element
}

export interface TextInputProps {
  /**
   * Forces the input to appear in a focused state.
   * Used when TextInput is a part of more complex components like Select and DatePicketInput, where the real focus may shift to a different element.
   * @default false
   */
  appearFocused?: boolean
  /**
   * Automatically focuses the input when the component mounts
   * @default false
   */
  autoFocus?: boolean
  /**
   * The color scheme of the input
   * @default 'default'
   */
  color?: TextInputColor
  /**
   * Ref for the visible container element.
   * Often used for ensuring that other elements (like dropdowns) are positioned relative to the input.
   */
  containerRef?: Ref<HTMLLabelElement>
  /**
   * Custom data attributes to be added to the input element
   */
  dataSet?: Record<string, string | undefined>
  /**
   * The initial value of the text input
   * @default ''
   */
  defaultValue?: string
  /**
   * If true, the input is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Icon, text or another element to display at the end of the input
   */
  endAdornment?: SvgIconComponent | string | ReactNode
  /**
   * Hint for the virtual keyboard's enter key
   */
  enterKeyHint?: EnterKeyHint
  /**
   * Whether the input is in an error state
   * @default false
   */
  error?: boolean
  /**
   * Additional descriptive text or error text to display below the input
   */
  helperText?: string
  /**
   * Specifies the virtual keyboard type for mobile devices
   */
  inputMode?: InputMode
  /**
   * Reference to the input or textarea element containing the value
   */
  inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>
  /**
   * The label for the input element
   */
  label: string
  /**
   * Number of visible text lines for multiline input.
   * Only applies when `multiline` is true
   * @default 0
   */
  lines?: number
  /**
   * Maximum value for number inputs
   */
  max?: string | number
  /**
   * Maximum number of lines for multiline input.
   * Only applies when `multiline` is true
   * @default 0
   */
  maxLines?: number
  /**
   * Minimum value for number inputs
   */
  min?: string | number
  /**
   * Renders a textarea instead of an input
   * @default false
   */
  multiline?: boolean
  /**
   * The name attribute for the input element
   */
  name?: string
  /**
   * Callback fired when the input loses focus
   */
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  /**
   * Callback fired when the clear button is clicked
   */
  onClear?: MouseEventHandler<HTMLButtonElement>
  /**
   * Callback fired when the input receives focus
   */
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  /**
   * Callback fired when the input value changes
   */
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  /**
   * Callback fired when the input is activated
   */
  onClick?: MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>
  /**
   * Callback fired when a key is pressed while the input is focused
   */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  /**
   * Makes the input read-only
   * @default false
   */
  readOnly?: boolean
  /**
   * Marks the input as required
   * @default false
   */
  required?: boolean
  /**
   * An internal prop, only used when the TextInput is part of Select component.
   * Changes onChange behavior to fire immediately instead of debounced and also uses the pointer cursor.
   * @default false
   */
  selectable?: boolean
  /**
   * The size of the input
   * @default 'medium'
   */
  size?: TextInputSize
  /**
   * Icon, text or another element to display at the start of the input
   */
  startAdornment?: SvgIconComponent | string | ReactNode
  /**
   * Tab order index for keyboard navigation
   */
  tabIndex?: number
  /**
   * The type attribute of the input element
   * @default 'text'
   */
  type?: TextInputType
  /**
   * The visual style variant of the text input
   * @default 'outlined'
   */
  variant?: TextInputVariant
}

type ExtraTextInputProps = TextInputProps & {
  'aria-activedescendant'?: AriaAttributes['aria-activedescendant']
  'aria-autocomplete'?: AriaAttributes['aria-autocomplete']
  'aria-controls'?: AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  'aria-labelledby'?: AriaAttributes['aria-labelledby']
  id?: string
  role?: string
}

type TextInputType =
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'url'
  | 'color'

const modes: { [InputType in TextInputType]: InputMode } = {
  email: 'email',
  number: 'numeric',
  password: 'text',
  search: 'search',
  tel: 'tel',
  text: 'text',
  url: 'url',
  color: 'none',
}

const TextInput = forwardRef<HTMLDivElement, TextInputProps>(
  (
    {
      appearFocused = false,
      autoFocus = false,
      color = 'default',
      containerRef,
      dataSet,
      defaultValue = '',
      disabled = false,
      endAdornment: EndAdornment,
      enterKeyHint,
      error = false,
      helperText,
      inputMode,
      inputRef,
      label,
      lines = 0,
      max,
      maxLines = 0,
      min,
      multiline = false,
      name,
      onBlur,
      onChange,
      onClick,
      onClear,
      onFocus,
      onKeyDown,
      readOnly = false,
      required = false,
      selectable = false,
      size = 'medium',
      startAdornment: StartAdornment,
      tabIndex,
      type = 'text',
      variant = 'outlined',
      ...rest
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const id = `coconut-text-input-${useId()}`
    const intl = useIntl()

    const labelRef = useRef<HTMLSpanElement>(null)
    const [hasValue, setHasValue] = useState(!!defaultValue)
    const [shrinkLabel, setShrinkLabel] = useState(!!StartAdornment)

    const localRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)
    const syncRef = useSyncRefs(localRef, inputRef)

    const handleClear: MouseEventHandler<HTMLButtonElement> = (event) => {
      event?.preventDefault()
      setHasValue(false)

      if (localRef.current) localRef.current.value = ''

      const input = localRef.current
      if (input) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(input, '')
        input.dispatchEvent(inputEvent)
      }

      onClear?.(event)
    }

    const debounced = useDebouncedCallback(
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange?.(event)
      },
      300,
    )

    const extraProps = rest as ExtraTextInputProps // to avoid some ts errors without exposing these props
    const status: TextInputStatus = disabled
      ? 'disabled'
      : error
      ? 'error'
      : 'default'

    const rows = lines ? lines : size === 'medium' ? 4 : 3

    const handleBlur = (
      event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      // handle focused styles manually in browsers that do not support ':focus-within'
      if (!isFocusWithinSupported() && !extraProps?.['aria-expanded']) {
        appearFocusedStyles[variant][status][color]
          .split(' ')
          .forEach((className) => {
            event.target.parentElement?.classList.remove(className)
          })
      }

      if (!disabled && !readOnly && localRef.current) {
        const trimmedValue = localRef.current.value.trim()
        if (localRef.current.value !== trimmedValue) {
          localRef.current.value = trimmedValue

          onChange?.({
            ...event,
            target: { ...localRef.current, value: trimmedValue },
          })
        }
      }

      onBlur?.(event)
    }

    const autosizeTextarea = (event: ChangeEvent<HTMLTextAreaElement>) => {
      const target = event.target

      target.rows = rows // so that we can auto shrink
      const lineHeight = parseInt(window.getComputedStyle(target).lineHeight)

      const newRows = Math.ceil(target.scrollHeight / lineHeight)

      if (newRows > rows) {
        if (maxLines <= 0 || newRows <= maxLines) {
          target.rows = newRows
        } else {
          target.rows = maxLines
        }
      }
    }

    const handleChange = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      event.persist()

      setHasValue(!!localRef.current?.value)

      if (selectable) {
        onChange?.(event)
      } else {
        debounced.callback(event)
      }

      if (multiline) {
        autosizeTextarea(event as ChangeEvent<HTMLTextAreaElement>)
      }
    }

    const handleFocus: FocusEventHandler<
      HTMLInputElement | HTMLTextAreaElement
    > = (event) => {
      // handle focused styles manually in browsers that do not support ':focus-within'
      if (!isFocusWithinSupported()) {
        appearFocusedStyles[variant][status][color]
          .split(' ')
          .forEach((className) => {
            event.target.parentElement?.classList.add(className)
          })
      }

      onFocus?.(event)
    }

    // If end adornment is long enough to overlap the label, shrink the label to the top
    useLayoutEffect(() => {
      if (
        !shrinkLabel &&
        labelRef.current &&
        localRef.current &&
        labelRef.current?.clientWidth + labelRef.current?.offsetLeft >
          localRef.current?.clientWidth + localRef.current?.offsetLeft
      ) {
        setShrinkLabel(true)
      }
    }, [EndAdornment, shrinkLabel])

    const commonProps = {
      'aria-activedescendant': extraProps?.['aria-activedescendant'],
      'aria-autocomplete': extraProps?.['aria-autocomplete'],
      'aria-controls': extraProps?.['aria-controls'],
      'aria-owns': extraProps?.['aria-controls'],
      'aria-describedby': helperText ? `helper-text-${id}` : undefined,
      'aria-expanded': extraProps?.['aria-expanded'],
      'aria-haspopup': extraProps?.['aria-haspopup'],
      'aria-invalid': error || undefined,
      'aria-labelledby':
        extraProps?.['aria-labelledby'] || `label-${extraProps?.id || id}`,
      autoFocus: autoFocus,
      className: clsx(
        baseClasses.input,
        inputVariants[status][color],
        multiline
          ? [
              multilineSizes[variant][size],
              'overflow-x-hidden break-words no-scrollbar resize-none',
            ]
          : [sizes[variant][size], 'truncate'],
        selectable ? 'cursor-pointer' : '',
      ),
      disabled: disabled,
      id: extraProps?.id,
      inputMode: inputMode || modes[type],
      name: name,
      onBlur: handleBlur,
      onChange: handleChange,
      onClick: onClick,
      onFocus: handleFocus,
      onKeyDown: onKeyDown,
      placeholder: '',
      readOnly,
      required,
      role: extraProps?.role,
      tabIndex: tabIndex,
      type: type,
      defaultValue: defaultValue,
    }

    return (
      <div className={baseClasses.container}>
        <label className="relative" ref={containerRef}>
          <div
            className={clsx(
              baseClasses.inputWrapper,
              focusedStyles[variant][status][color],
              appearFocused
                ? appearFocusedStyles[variant][status][color]
                : variants[variant][status][color],
              multiline ? '' : parentSizes[variant][size],
            )}
            ref={ref}
          >
            {StartAdornment ? (
              <span
                className={clsx(
                  baseClasses.start,
                  variant === 'filled' ? 'pt-3' : null,
                  multiline
                    ? adornments.multilineSize[variant][size]
                    : adornments.size[variant][size],
                  adornments.text[status][color],
                  typeof StartAdornment === 'function' &&
                    'uiName' in StartAdornment &&
                    StartAdornment.uiName === 'SvgIcon'
                    ? adornments.icon
                    : null,
                )}
              >
                {typeof StartAdornment === 'function' &&
                'uiName' in StartAdornment &&
                StartAdornment.uiName === 'SvgIcon' ? (
                  <StartAdornment color="inherit" />
                ) : (
                  StartAdornment
                )}
              </span>
            ) : null}
            {multiline ? (
              <textarea {...commonProps} ref={syncRef} rows={rows} />
            ) : (
              <input
                {...commonProps}
                {...toDataSet(dataSet)}
                enterKeyHint={enterKeyHint}
                max={max}
                min={min}
                ref={syncRef}
              />
            )}
            {onClear && hasValue ? (
              <span
                className={clsx(
                  baseClasses.end,
                  multiline
                    ? adornments.multilineSize[variant][size]
                    : adornments.size[variant][size],
                  adornments.text[status][color],
                  EndAdornment ? 'mr-0.5' : 'mr-2.5',
                )}
              >
                <IconButton
                  aria-label={intl.formatMessage(
                    { id: 'TextInput.clear_labelled_input' },
                    { label },
                  )}
                  color={color}
                  onClick={handleClear}
                  size="small"
                >
                  <Close />
                </IconButton>
              </span>
            ) : null}
            {EndAdornment ? (
              <span
                className={clsx(
                  baseClasses.end,
                  multiline
                    ? adornments.multilineSize[variant][size]
                    : adornments.size[variant][size],
                  adornments.text[status][color],
                  typeof EndAdornment === 'function' &&
                    'uiName' in EndAdornment &&
                    EndAdornment.uiName === 'SvgIcon'
                    ? adornments.icon
                    : null,
                  'mr-3',
                )}
              >
                {typeof EndAdornment === 'function' &&
                'uiName' in EndAdornment &&
                EndAdornment.uiName === 'SvgIcon' ? (
                  <EndAdornment color="inherit" />
                ) : (
                  EndAdornment
                )}
              </span>
            ) : null}
            <span
              className={clsx(
                'input-label',
                variant === 'outlined'
                  ? 'input-label-outlined'
                  : 'input-label-filled',
                size === 'medium' ? 'input-label-medium' : 'input-label-small',
                baseClasses.label,
                disabled ? 'cursor-not-allowed' : 'cursor-text',
                appearFocused
                  ? `${labels.focused[variant]} ${labelColors.focused[variant][status][color]}`
                  : shrinkLabel
                  ? `${labels.value[variant]} ${labelColors.value[variant][status][color]}`
                  : `${labels.inactive[variant]} ${labelColors.inactive[variant][status][color]}`,
                appearFocused || shrinkLabel
                  ? labelSpacing.focused[variant][size]
                  : labelSpacing.inactive[variant][size],
              )}
              id={`label-${extraProps?.id || id}`}
              ref={labelRef}
            >
              {label}
            </span>
          </div>
        </label>
        <div
          aria-live={error ? 'assertive' : 'off'}
          className={
            helperText
              ? clsx(
                  baseClasses.help,
                  labelColors.value[variant][status][color],
                )
              : 'sr-only'
          }
          id={`helper-text-${id}`}
        >
          {helperText ? helperText : null}
        </div>
      </div>
    )
  },
)
export default TextInput
